"use strict";

import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import Swiper from "swiper";
import { onReady } from "../core/ready.js";

let storyScrollController = null;
let qualityArrowTween = null;
let wordsSliderController = null;
let heroTitleDecoTween = null;
let heroDecoStartCleanup = null;
let qualityArrowStartCleanup = null;
let refreshCleanup = null;
const LOADER_HIDE_FALLBACK_MS = 500;

gsap.registerPlugin(ScrollTrigger);

function runAfterLoaderHidden(cb) {
    const loader = document.getElementById("siteLoader");
    if (!loader || !window.getComputedStyle) {
        cb();
        return;
    }

    const styles = window.getComputedStyle(loader);
    const opacity = parseFloat(styles.opacity || "0");
    const isStillVisible = loader.classList.contains("is-visible")
        || opacity > 0.01
        || styles.visibility !== "hidden";

    if (!isStillVisible) {
        cb();
        return;
    }

    let done = false;
    const finish = () => {
        if (done) return;
        done = true;
        loader.removeEventListener("transitionend", onTransitionEnd);
        cb();
    };
    const onTransitionEnd = (event) => {
        if (event.target !== loader) return;
        if (event.propertyName && event.propertyName !== "opacity") return;
        finish();
    };

    loader.addEventListener("transitionend", onTransitionEnd);
    window.setTimeout(finish, LOADER_HIDE_FALLBACK_MS);
}

function setupScrollTriggerRefresh({ slider, swiper, storyVideo }) {
    let refreshScheduled = false;
    const cleanupFns = [];
    const timeoutIds = [];
    const markRefresh = () => requestRefresh();

    const addListener = (target, event, handler, options) => {
        if (!target?.addEventListener) return;
        target.addEventListener(event, handler, options);
        cleanupFns.push(() => target.removeEventListener(event, handler, options));
    };

    const requestRefresh = () => {
        if (refreshScheduled) return;
        refreshScheduled = true;
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                refreshScheduled = false;
                const lenis = window.__lenis;
                if (lenis && typeof lenis.resize === "function") {
                    lenis.resize();
                }
                if (lenis && typeof lenis.scrollTo === "function") {
                    const currentY = window.scrollY || window.pageYOffset || 0;
                    lenis.scrollTo(currentY, { immediate: true, force: true });
                }
                ScrollTrigger.refresh();
                ScrollTrigger.update();
            });
        });
    };

    const schedulePostInitRefresh = () => {
        markRefresh();
        timeoutIds.push(window.setTimeout(markRefresh, 140));
        timeoutIds.push(window.setTimeout(markRefresh, 420));
        timeoutIds.push(window.setTimeout(markRefresh, 900));
        timeoutIds.push(window.setTimeout(markRefresh, 1600));
    };

    if (document.readyState === "complete") markRefresh();
    else addListener(window, "load", markRefresh, { once: true });
    addListener(window, "pageshow", markRefresh, { once: true });

    if (document.fonts?.ready) {
        document.fonts.ready.then(markRefresh).catch(() => {});
    }

    addListener(window, "resize", markRefresh);
    addListener(window, "scroll", markRefresh, { once: true, passive: true });
    addListener(window, "wheel", markRefresh, { once: true, passive: true });
    addListener(window, "touchmove", markRefresh, { once: true, passive: true });

    if (storyVideo) {
        if (storyVideo.readyState >= 1) markRefresh();
        else addListener(storyVideo, "loadedmetadata", markRefresh, { once: true });
    }

    if (swiper?.on && swiper?.off) {
        swiper.on("imagesReady", markRefresh);
        swiper.on("init", markRefresh);
        cleanupFns.push(() => {
            swiper.off("imagesReady", requestRefresh);
            swiper.off("init", requestRefresh);
        });
    } else if (slider) {
        markRefresh();
    }

    schedulePostInitRefresh();

    return () => {
        cleanupFns.forEach((fn) => fn());
        cleanupFns.length = 0;
        timeoutIds.forEach((id) => window.clearTimeout(id));
        timeoutIds.length = 0;
    };
}

function setupScrollLinkedYMotion({ element, startElement, endElement, fromVH, toVH }) {
    if (!element || !startElement || !endElement) return null;

    const cleanupFns = [];
    const addListener = (target, event, handler, options) => {
        if (!target?.addEventListener) return;
        target.addEventListener(event, handler, options);
        cleanupFns.push(() => target.removeEventListener(event, handler, options));
    };
    const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

    const update = () => {
        const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
        if (!viewportH) return;

        const scrollY = window.scrollY || window.pageYOffset || 0;
        const startTopAbs = startElement.getBoundingClientRect().top + scrollY;
        const endBottomAbs = endElement.getBoundingClientRect().bottom + scrollY;
        const startScroll = startTopAbs - viewportH; // top bottom
        const endScroll = endBottomAbs; // bottom top
        const range = Math.max(1, endScroll - startScroll);
        const progress = clamp((scrollY - startScroll) / range, 0, 1);
        const fromY = (fromVH / 100) * viewportH;
        const toY = (toVH / 100) * viewportH;

        gsap.set(element, { y: fromY + (toY - fromY) * progress });
    };

    addListener(window, "scroll", update, { passive: true });
    addListener(window, "resize", update);
    addListener(window, "load", update, { once: true });
    addListener(window, "pageshow", update);

    if (document.fonts?.ready) {
        document.fonts.ready.then(update).catch(() => {});
    }

    const lenis = window.__lenis;
    if (lenis?.on && lenis?.off) {
        lenis.on("scroll", update);
        cleanupFns.push(() => lenis.off("scroll", update));
    }

    requestAnimationFrame(() => requestAnimationFrame(update));
    update();

    return {
        update,
        destroy: () => {
            cleanupFns.forEach((fn) => fn());
            cleanupFns.length = 0;
        }
    };
}

function runWhenNearViewportMiddle(target, callback, options = {}) {
    if (!target || typeof callback !== "function") return () => {};
    const immediateOnTopLoad = options.immediateOnTopLoad === true;

    let done = false;
    const cleanupFns = [];
    const addListener = (event, handler, options) => {
        window.addEventListener(event, handler, options);
        cleanupFns.push(() => window.removeEventListener(event, handler, options));
    };

    const check = () => {
        if (done || !target?.getBoundingClientRect) return;
        const rect = target.getBoundingClientRect();
        const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
        const middleLine = viewportH * 0.5;
        const intersectsMiddle = rect.top <= middleLine && rect.bottom >= middleLine;
        if (!intersectsMiddle) return;
        done = true;
        callback();
        cleanup();
    };

    let observer = null;
    if ("IntersectionObserver" in window) {
        observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry?.isIntersecting) return;
                check();
            },
            {
                root: null,
                rootMargin: "-45% 0px -45% 0px",
                threshold: 0
            }
        );
        observer.observe(target);
        cleanupFns.push(() => observer?.disconnect());
    }

    addListener("scroll", check, { passive: true });
    addListener("resize", check);
    addListener("load", check, { once: true });
    addListener("pageshow", check);

    const lenis = window.__lenis;
    if (lenis?.on && lenis?.off) {
        lenis.on("scroll", check);
        cleanupFns.push(() => lenis.off("scroll", check));
    }

    const cleanup = () => {
        cleanupFns.forEach((fn) => fn());
        cleanupFns.length = 0;
    };

    if (immediateOnTopLoad) {
        const initialY = window.scrollY || window.pageYOffset || 0;
        if (initialY <= 12) {
            done = true;
            callback();
            return cleanup;
        }
    }

    requestAnimationFrame(() => requestAnimationFrame(check));
    check();

    return cleanup;
}

function prepareHeroTitleDecoAnimation() {
    const deco = document.querySelector(".about-hero-title-deco");
    if (!deco) return null;

    const paths = Array.from(deco.querySelectorAll("path"));
    if (!paths.length) return null;

    gsap.set(deco, { opacity: 0, x: 0, y: 0, rotate: 0 });

    paths.forEach((path) => {
        const pathLength = path.getTotalLength();
        gsap.set(path, {
            strokeDasharray: pathLength,
            strokeDashoffset: pathLength
        });
    });

    return { deco, paths };
}

function setupHeroTitleDecoAnimation() {
    if (heroTitleDecoTween) {
        heroTitleDecoTween.kill();
        heroTitleDecoTween = null;
    }

    const prepared = prepareHeroTitleDecoAnimation();
    if (!prepared) return;
    const { deco, paths } = prepared;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        gsap.set(deco, { opacity: 1, x: 0, y: 0, rotate: 0 });
        gsap.set(paths, { strokeDasharray: "", strokeDashoffset: 0 });
        return;
    }

    heroTitleDecoTween = gsap.timeline({
        defaults: { ease: "none" }
    });

    heroTitleDecoTween.set(deco, { opacity: 1 }, 0);

    paths.forEach((path, index) => {
        heroTitleDecoTween.to(
            path,
            {
                strokeDashoffset: 0,
                duration: 0.3
            },
            index === 0 ? 0 : ">"
        );
    });
}

function setupHeroTitleDecoAnimationOnViewportMiddle() {
    if (heroDecoStartCleanup) {
        heroDecoStartCleanup();
        heroDecoStartCleanup = null;
    }

    const deco = document.querySelector(".about-hero-title-deco");
    if (!deco) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        setupHeroTitleDecoAnimation();
        return;
    }

    heroDecoStartCleanup = runWhenNearViewportMiddle(
        deco,
        setupHeroTitleDecoAnimation,
        { immediateOnTopLoad: true }
    );
}

function initAboutPage() {
    const about = document.querySelector(".about-main");
    if (!about) return;

    about.classList.add("about-ready");
    prepareHeroTitleDecoAnimation();
    runAfterLoaderHidden(setupHeroTitleDecoAnimationOnViewportMiddle);

    const slider = document.getElementById("aboutWordsSlider");
    let sliderInstance = slider?.swiper || null;

    if (slider && !slider.dataset.swiperReady) {
        slider.dataset.swiperReady = "1";
        sliderInstance = new Swiper(slider, {
            loop: false,
            slidesPerView: 1.2,
            spaceBetween: 16,
            centeredSlides: false,
            breakpoints: {
                576: { slidesPerView: 2.2, spaceBetween: 16 },
                768: { slidesPerView: 3, spaceBetween: 18 },
                992: { slidesPerView: 4, spaceBetween: 20 },
                1200: { slidesPerView: 5, spaceBetween: 20 }
            }
        });
    }

    if (refreshCleanup) {
        refreshCleanup();
        refreshCleanup = null;
    }

    const storyPanel = document.querySelector(".about-story");
    const storyMedia = storyPanel?.querySelector(".about-story-media");
    const storyVideo = storyPanel?.querySelector("video");

    if (storyScrollController) {
        storyScrollController.destroy();
        storyScrollController = null;
    }

    if (storyPanel && storyMedia) {
        storyScrollController = setupScrollLinkedYMotion({
            element: storyMedia,
            startElement: storyPanel,
            endElement: storyPanel,
            fromVH: -10,
            toVH: 10
        });

        storyVideo?.addEventListener("loadedmetadata", () => {
            storyScrollController?.update();
        });
    }

    const qualityArrow = document.querySelector(".about-quality-arrow");
    const qualityPath = qualityArrow?.querySelector("path");
    const qualityMediaQuery = window.matchMedia("(min-width: 768px)");

    function setupQualityArrowAnimation() {
        if (qualityArrowTween) {
            qualityArrowTween.scrollTrigger?.kill();
            qualityArrowTween.kill();
            qualityArrowTween = null;
        }
        if (qualityArrowStartCleanup) {
            qualityArrowStartCleanup();
            qualityArrowStartCleanup = null;
        }

        if (!qualityArrow || !qualityPath) return;

        if (!qualityMediaQuery.matches) {
            qualityPath.style.strokeDasharray = "";
            qualityPath.style.strokeDashoffset = "0";
            gsap.set(qualityArrow, { x: 0, opacity: 1 });
            return;
        }

        const pathLength = qualityPath.getTotalLength();
        gsap.set(qualityPath, {
            strokeDasharray: pathLength,
            strokeDashoffset: pathLength
        });

        gsap.set(qualityArrow, { x: -12, opacity: 0 });

        qualityArrowStartCleanup = runWhenNearViewportMiddle(qualityArrow, () => {
            qualityArrowTween = gsap.timeline();
            qualityArrowTween
                .to(
                    qualityArrow,
                    { x: 0, opacity: 1, duration: 0.8, ease: "power2.out" },
                    0
                )
                .to(
                    qualityPath,
                    { strokeDashoffset: 0, duration: 1.1, ease: "power2.out" },
                    0
                );
        });
    }

    setupQualityArrowAnimation();
    qualityMediaQuery.addEventListener("change", () => {
        setupQualityArrowAnimation();
        ScrollTrigger.refresh();
    });

    if (wordsSliderController) {
        wordsSliderController.destroy();
        wordsSliderController = null;
    }

    const wordsFollow = document.querySelector(".about-words-follow");
    const wordsSection = document.querySelector(".about-words");
    const wordsMediaQuery = window.matchMedia("(min-width: 768px)");

    function setupWordsSliderAnimation() {
        if (wordsSliderController) {
            wordsSliderController.destroy();
            wordsSliderController = null;
        }

        if (!slider || !wordsFollow) return;

        if (!wordsMediaQuery.matches) {
            gsap.set(slider, { y: 0 });
            return;
        }

        const wordsStartTrigger = wordsSection || wordsFollow;
        wordsSliderController = setupScrollLinkedYMotion({
            element: slider,
            startElement: wordsStartTrigger,
            endElement: wordsFollow,
            fromVH: -24,
            toVH: 0
        });
    }

    if (slider && wordsFollow) {
        setupWordsSliderAnimation();
        wordsMediaQuery.addEventListener("change", () => {
            setupWordsSliderAnimation();
            ScrollTrigger.refresh();
        });
    }

    refreshCleanup = setupScrollTriggerRefresh({
        slider,
        swiper: sliderInstance,
        storyVideo
    });

}

onReady(initAboutPage);
